import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";

import { faRadar } from "@fortawesome/pro-solid-svg-icons/faRadar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Dropdown from "antd/es/dropdown";
import Menu from "antd/es/menu";
import { defineMessage, defineMessages, useIntl } from "react-intl";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import FilterModel from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import { convertCoordinatesToLongLat } from "@mapmycustomers/shared/util/geo/GeoService";

import { useMap } from "@app/component/map/FeaturedMap";
import RoundButton from "@app/component/RoundButton";
import localSettings from "@app/config/LocalSettings";
import { getPosition } from "@app/store/iam";
import { reverseGeocodeAddress } from "@app/store/location/actions";
import { RootState } from "@app/store/rootReducer";
import { METERS_IN_MILE } from "@app/util/consts";
import useAnalytics from "@app/util/contexts/useAnalytics";

const DISTANCES_KM = [1, 5, 10, 25, 50, 100];
const DISTANCES_MILES = [1, 2, 5, 10, 25, 50];
const CUSTOM_KEY = -1;

const unitMessages = defineMessages({
  km: {
    id: "component.quickFilters.nearby.km",
    defaultMessage: "Within {value}km",
    description: "Dropdown menu label in Nearby Quick Filter for km units",
  },
  miles: {
    id: "component.quickFilters.nearby.miles",
    defaultMessage: "Within {value} {value, plural, one {mile} other {miles}}",
    description: "Dropdown menu label in Nearby Quick Filter for mile units",
  },
});

const customLabelMessage = defineMessage({
  id: "component.quickFilters.nearby.custom",
  defaultMessage: "Custom",
  description: "Custom option in dropdown in Nearby Quick filter",
});

interface Props {
  fieldName?: string;
  filter: FilterModel;
  onReverseGeocodeAddress: typeof reverseGeocodeAddress;
  onSetCustomFilters: (customFilters: Partial<FilterModel>) => void;
  onUpdateFilterModel: (filterModel: Partial<FilterModel>) => void;
  userPosition?: GeolocationPosition;
}

const NearbyFilter: React.FC<Props> = ({
  fieldName = "geoPoint",
  filter,
  onReverseGeocodeAddress,
  onSetCustomFilters,
  onUpdateFilterModel,
  userPosition,
}) => {
  const analytics = useAnalytics();
  const intl = useIntl();

  const doesUseSiUnits = localSettings.doesUseSiUnits();
  const distances = doesUseSiUnits ? DISTANCES_KM : DISTANCES_MILES;
  const formatValue = useCallback(
    (value: number) =>
      intl.formatMessage(doesUseSiUnits ? unitMessages.km : unitMessages.miles, { value }),
    [doesUseSiUnits, intl]
  );

  const [nearbyRadius, setNearbyRadius] = useState<number | undefined>(undefined);

  // location is not necessary to have, but is nice to have to show user's address in AreaFilter toolpanel
  const location = useRef<string>("");
  useEffect(() => {
    if (userPosition) {
      onReverseGeocodeAddress({
        callback: (result) => {
          location.current = result.address.formattedAddress ?? "";
        },
        coordinates: convertCoordinatesToLongLat(userPosition.coords),
      });
    }
  }, [location, onReverseGeocodeAddress, userPosition]);

  const { map } = useMap();

  const handleMenuClick = useCallback(
    ({ key }) => {
      const value = parseInt(key, 10);
      if (value === CUSTOM_KEY) {
        onSetCustomFilters({ [fieldName]: { operator: FilterOperator.IN_AREA, value: undefined } });
        analytics.clicked(["Nearby", "Custom"]);
        return;
      }

      // convert km or mile into meters
      const radius = doesUseSiUnits ? value * 1000 : value * METERS_IN_MILE;

      // 0 value (Clear option) is considered to remove filter
      if (value && userPosition?.coords) {
        const circle = new google.maps.Circle({
          center: { lat: userPosition.coords.latitude, lng: userPosition.coords.longitude },
          radius,
        });

        map?.fitBounds(circle.getBounds()!);

        onUpdateFilterModel({
          [fieldName]: {
            operator: FilterOperator.IN_AREA,
            value: {
              distanceInMeters: radius,
              latitude: userPosition.coords.latitude,
              location: location.current,
              longitude: userPosition.coords.longitude,
            },
          },
        });
        analytics.clicked(["Nearby", `${value}${doesUseSiUnits ? "km" : "mi"}`]);
      } else {
        onUpdateFilterModel({ [fieldName]: undefined });
      }
    },
    [
      analytics,
      doesUseSiUnits,
      fieldName,
      map,
      onSetCustomFilters,
      onUpdateFilterModel,
      userPosition,
    ]
  );

  useEffect(() => {
    if (filter && fieldName in filter) {
      const filterCondition = filter[fieldName];
      if (filterCondition && filterCondition.operator === FilterOperator.IN_AREA) {
        // if it's area filter and its params do not match ours, reset nearby filter state
        const distanceInUnits =
          filterCondition.value.distanceInMeters / (doesUseSiUnits ? 1000 : METERS_IN_MILE);
        setNearbyRadius(
          !distances.includes(distanceInUnits) ||
            // compare filter coords and user coords with some precision, because user position
            // can slightly fluctuate, but we still wanna consider this button active
            // 1 degree lat/long is 111.32km, we are using 1m precision for latitude and 10m precision for longitude as longitude gets smaller approaching the poles.
            Math.abs(filterCondition.value.latitude - (userPosition?.coords.latitude ?? 0)) >
              0.00001 ||
            Math.abs(filterCondition.value.longitude - (userPosition?.coords.longitude ?? 0)) >
              0.0001
            ? undefined
            : filterCondition.value.distanceInMeters
        );
        return;
      }
    }
    // if any other filter is applied to this field or no filter at all, reset nearby state
    setNearbyRadius(undefined);
  }, [distances, doesUseSiUnits, fieldName, filter, setNearbyRadius, userPosition]);

  return (
    <Dropdown
      overlay={
        <Menu onClick={handleMenuClick}>
          {!!nearbyRadius && (
            <Menu.Item key={0}>
              <b>
                {intl.formatMessage({
                  id: "map.filters.quickFilters.nearby.clear",
                  defaultMessage: "Clear filter",
                  description: "Clear option in dropdown in Nearby Quick filter",
                })}
              </b>
            </Menu.Item>
          )}
          {distances.map((value) => (
            <Menu.Item disabled={!userPosition?.coords} key={value}>
              {formatValue(value)}
            </Menu.Item>
          ))}
          <Menu.Item key={CUSTOM_KEY}>{intl.formatMessage(customLabelMessage)}</Menu.Item>
        </Menu>
      }
      trigger={["click"]}
    >
      <RoundButton active={!!nearbyRadius} hasMenu leftIcon={<FontAwesomeIcon icon={faRadar} />}>
        {intl.formatMessage(
          {
            id: "component.quickFilters.nearby.title",
            defaultMessage: "Nearby{isSet, select, true {: {value}} other {}}",
            description: "Nearby placeholder",
          },
          {
            isSet: !!nearbyRadius,
            value: nearbyRadius
              ? formatValue(nearbyRadius / (doesUseSiUnits ? 1000 : METERS_IN_MILE))
              : "",
          }
        )}
      </RoundButton>
    </Dropdown>
  );
};

const mapStateToProps = (state: RootState) => ({
  userPosition: getPosition(state),
});

const mapDispatchToProps = {
  onReverseGeocodeAddress: reverseGeocodeAddress,
};
export default connect(mapStateToProps, mapDispatchToProps)(NearbyFilter);
